import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	deleteRdo,
	finalizeRdo,
	openRdoRevision,
	retryRdo,
} from "@/adapters/api";
import type { Rdo } from "../types.ts";
import { toRdo } from "../utils/map.ts";
import { rdoQueryKey } from "./use-rdo.ts";
import { rdosQueryKey } from "./use-rdos.ts";

type RdoAction = (constructionId: string, rdoId: string) => Promise<unknown>;

function useRdoTransition(
	constructionId: string | undefined,
	rdoId: string | undefined,
	action: RdoAction,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (): Promise<Rdo> => {
			if (!constructionId || !rdoId) throw new Error("missing rdo");
			const rdo = await action(constructionId, rdoId);
			return toRdo(rdo as Parameters<typeof toRdo>[0]);
		},
		onSuccess: (rdo) => {
			queryClient.setQueryData(rdoQueryKey(constructionId, rdo.id), rdo);
			if (rdo.id !== rdoId) {
				queryClient.invalidateQueries({
					queryKey: rdoQueryKey(constructionId, rdoId),
				});
			}
			queryClient.invalidateQueries({ queryKey: rdosQueryKey(constructionId) });
		},
	});
}

export function useFinalizeRdo(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	return useRdoTransition(constructionId, rdoId, finalizeRdo);
}

export function useRetryRdo(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	return useRdoTransition(constructionId, rdoId, retryRdo);
}

export function useOpenRdoRevision(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	return useRdoTransition(constructionId, rdoId, openRdoRevision);
}

export function useDeleteRdo(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!constructionId || !rdoId) throw new Error("missing rdo");
			await deleteRdo(constructionId, rdoId);
		},
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: rdoQueryKey(constructionId, rdoId) });
			queryClient.invalidateQueries({ queryKey: rdosQueryKey(constructionId) });
		},
	});
}
